/**
 * Theme Variables Utility
 *
 * This utility function applies theme variables from the client configuration
 * to CSS custom properties, replacing the individual theme variable files.
 */

import clientConfig from '../../../../config/client-config';
import { AvailableThemes } from '../../../../config/client-config.types';

/**
 * Assigns theme variables to CSS custom properties based on the selected theme
 * @param themeName - The theme to apply variables for
 */
export function assignThemeVariables(themeName: AvailableThemes): void {
  // Map theme names to configuration keys
  const themeKey = themeName.toLowerCase() as keyof typeof clientConfig.themes;

  // Handle special case for CHRISTMASS -> christmas
  const configKey = themeName === 'CHRISTMASS' ? 'christmas' : themeKey;

  const themeConfig = clientConfig.themes[configKey as keyof typeof clientConfig.themes];

  if (!themeConfig) {
    console.warn(`Theme configuration not found for: ${themeName}`);
    return;
  }

  const variables = themeConfig.variables;

  // Apply all theme variables to CSS custom properties
  document.documentElement.style.setProperty('--bg-color', variables.bgColor);
  document.documentElement.style.setProperty('--sub-bg-color', variables.subBgColor);

  if (variables.alterBgColor) {
    document.documentElement.style.setProperty('--alter-bg-color', variables.alterBgColor);
  }

  if (variables.alterGradBgColor) {
    document.documentElement.style.setProperty('--alter-grad-bg-color', variables.alterGradBgColor);
  }

  document.documentElement.style.setProperty('--alert-bg', variables.alertBg);
  document.documentElement.style.setProperty('--alert-bg-green', variables.alertBgGreen);
  document.documentElement.style.setProperty('--alert-bg-yellow', variables.alertBgYellow);

  if (variables.alertBgRed) {
    document.documentElement.style.setProperty('--alert-bg-red', variables.alertBgRed);
  }

  document.documentElement.style.setProperty('--text-color', variables.textColor);
  document.documentElement.style.setProperty('--low-text-color', variables.lowTextColor);
  document.documentElement.style.setProperty('--main-color', variables.mainColor);
  document.documentElement.style.setProperty('--logo-color', variables.logoColor);
  document.documentElement.style.setProperty('--alt-color', variables.altColor);
  document.documentElement.style.setProperty('--rs-steps-state-process', variables.rsStepsStateProcess);
  document.documentElement.style.setProperty('--rs-steps-state-finish', variables.rsStepsStateFinish);
  document.documentElement.style.setProperty('--login-bg', variables.loginBg);

  if (variables.iconInvert !== undefined) {
    document.documentElement.style.setProperty('--icon-invert', variables.iconInvert);
  }

  if (variables.iconInvertActive) {
    document.documentElement.style.setProperty('--icon-invert-active', variables.iconInvertActive);
  }

  if (variables.mainColorHover) {
    document.documentElement.style.setProperty('--main-color-hover', variables.mainColorHover);
  }

  if (variables.navbarWidth) {
    document.documentElement.style.setProperty('--navbar-width', variables.navbarWidth);
  }
}

/**
 * Legacy function names for backward compatibility
 * These maintain the existing API while using the new configuration system
 */
export const assignLightVariables = () => assignThemeVariables('LIGHT');
export const assignDarkVariables = () => assignThemeVariables('DARK');
export const assignHighContrastVariables = () => assignThemeVariables('HIGH_CONTRAST');
export const assignChristmassVariables = () => assignThemeVariables('CHRISTMASS');
